import React, { useState, useMemo, useContext, useRef, useEffect } from "react";
import { useParams, Link, useLocation, matchPath } from "react-router-dom";
import { routeNames } from "../../../routeSegments";
import ExcelDownloader from "../../shared/ExcelDownloader";
import { exportExcel } from "../../../Config/config";
import routePathSegments from "../../../routeSegments";
import moment from "moment";
import OverflowToolTip from "../../shared/OverflowToolTip";
import Constants from "../../../Constants";
import ConfirmPopup from "../../../Utils/ConfirmPopup";
import useModule from "../../../hooks/useModule";
import { UserContext } from "../../../Contexts/UserContext";
import NewLogo from "../../NewLogo";
import AllDataTable from "../../DataTables/AllDataTable";
import MonthPickerComponent from "../../../Utils/MonthPickerComponent";
import { PeFilterContext } from "../../../Contexts/PeFilterContext";

function AllInvestorPortfolioIPO({ isLoading, portfolio, investorName }) {
  console.log(isLoading, portfolio, investorName);
  const InvIpoRef = useRef(null);
  const { investorId } = useParams();
  const [showXlDownload, setshowXlDownload] = useState(false);
  const location = useLocation();
  const { primaryMasterId, isPE } = useModule();
  const { isStudent, isDirectoryUser, isTrialExport } = useContext(UserContext);
  const [alert, setalert] = useState({ message: "", show: false });
  const { monthPickerValue, setMonthPickerValue } = useContext(PeFilterContext);

  /* The above code is defining a custom sorting function called `customStringSortMemo` using the
   `useMemo` hook in React. This function takes in two rows (`rowA` and `rowB`), a column ID, and a
   boolean value `desc` indicating whether the sorting should be in descending order. */
  const customStringSortMemo = useMemo(() => {
    return (rowA, rowB, columnId, desc) => {
      const defaultVal = desc ? "--" : "A";
      const r1 = rowA?.values[columnId]?.name;
      const r2 = rowB?.values[columnId]?.name;
      return (r1 ?? defaultVal).localeCompare(r2 ?? defaultVal);
    };
  }, []);

  const peMatch = matchPath(`${routePathSegments.pe}/*`, location.pathname);

  const dealDateSort = useMemo(() => {
    return (rowA, rowB, columnId, desc) => {
      return moment
        .utc(rowA?.values[columnId].date)
        .diff(moment.utc(rowB?.values[columnId].date));
    };
  }, []);

  const addCommasToNumber = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const onConfirmation = (flag) => {
    setalert({ show: false, message: "" });
  };

  const onBlurColumnClick = (cell) => {
    setalert({
      show: true,
      message: Constants.maSubscriptionMsg,
      isAlertOnly: true,
      closeBtnName: "OK",
    });
  };

  const columns = useMemo(
    () => [
      {
        id: "companyName",
        Header: "Company Name",
        accessor: "companyName",
        className: "clickable text-left lg:relative sticky left-0 bg-white",
        headerClassName: "text-left lg:relative sticky left-0 bg-[#EDEDED]",
        style: { minWidth: "220px", width: "220px" },
        headerStyle: { minWidth: "220px", width: "220px" },
        Cell: ({ value }) => {
          if (!value) return "--";
          return (
            <div className="leading-[2rem] font-sans_book_body items-center justify-center">
              <NewLogo name={value.name} id={value.id} menu="pe" />
              <OverflowToolTip
                className="w-full"
                component={
                  <Link
                    className="text-start lg:inline flex hover:text-[#BC8B20]"
                    state={{ cameFrom: location.state?.prevPath }}
                    to={
                      peMatch != null
                        ? routeNames.peCompanyFundById(value?.id)
                        : routeNames.vcCompanyFundById(value?.id)
                    }
                  >
                    {value.name ?? "--"}
                  </Link>
                }
              />
            </div>
          );
        },
        sortType: customStringSortMemo,
      },
      {
        id: "sector",
        Header: "Sector",
        accessor: "sector",
        className: "text-left",
        style: {
          width: "250px",
          maxWidth: "250px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
        headerStyle: { width: "250px", maxWidth: "250px" },
        Cell: ({ value }) => (
          <div className="leading-[2rem] font-sans_book_body overflow-hidden text-overflow-ellipsis whitespace-nowrap">
            <OverflowToolTip className="w-full" component={value || "--"} />
          </div>
        ),
      },
      {
        id: "dealDate",
        Header: "Deal Date",
        accessor: "dealDate",
        Cell: ({ value }) => {
          if (!value) return "--";
          return (
            <Link
              target="_blank"
              state={{ cameFrom: location.state?.prevPath ?? "" }}
              onClick={(e) => {
                if (isDirectoryUser) {
                  e.preventDefault();
                  e.stopPropagation();
                  onBlurColumnClick();
                }
              }}
              to={
                primaryMasterId === Constants.primaryMasterIds.peId
                  ? routeNames.peCompanyBackedIPOSDealById(
                      value.id,
                      value.dealId
                    )
                  : routeNames.vcCompanyBackedIPOSDealById(
                      value.id,
                      value.dealId
                    )
              }
            >
              {value.date ? moment(value.date).format("MMM YYYY") : "--"}
            </Link>
          );
        },
        sortType: dealDateSort,
      },
      {
        id: "ipoSize",
        Header: "IPO Size (US$ M)",
        accessor: "ipoSize",
        className: "text-right pr-[2.6rem] w-[0%]",
        Cell: ({ value }) => (value ? addCommasToNumber(value) : "--"),
      },
      {
        id: "investorSaleInIpo",
        Header: "Investor Sale in IPO?",
        accessor: "investorSaleInIpo",
        headerClassName: "pl-[8rem]",
        className: "pl-[8rem]",
      },
      {
        id: "ipoValuation",
        Header: "IPO Valuation (US$ M)",
        accessor: "ipoValuation",
        className: "text-right pr-[2.5rem] w-[0%]",
        Cell: ({ value }) => (value ? addCommasToNumber(value) : "--"),
      },
    ],
    [
      peMatch,
      location.state?.prevPath,
      isDirectoryUser,
      onBlurColumnClick,
      primaryMasterId,
    ]
  );

  const rowData = useMemo(() => {
    const rows = [];

    const startDate = new Date(monthPickerValue[0]);
    const endDate = new Date(monthPickerValue[1]);

    portfolio?.forEach((company) => {
      const dealDateStr = company?.date;
      const dealDate = dealDateStr ? new Date(dealDateStr) : null;

      const isWithinDateRange =
        dealDate && dealDate >= startDate && dealDate <= endDate;
      const isMatchingVCType = isPE === "vc" ? company.is_vc : !company.is_vc;

      if (isWithinDateRange && isMatchingVCType) {
        rows.push({
          companyName: {
            name: company?.company?.name,
            id: company?.company?.id,
            dealId: company?.id,
            icon_path: company?.icon_path,
          },
          sector: company?.company?.sector || "--",
          dealDate: {
            date: company?.date,
            dealId: company?.id,
            id: company?.company?.id,
          },
          ipoSize: company?.ipo_size === 0 ? "" : company?.ipo_size,
          investorSaleInIpo: company?.is_investor_sale ? "Yes" : "No",
          ipoValuation:
            company?.ipo_valuation === 0 ? "" : company?.ipo_valuation,
        });
      }
    });

    setshowXlDownload(rows.length !== 0);

    return rows;
  }, [portfolio, monthPickerValue, isPE]);

  const initialSort = useMemo(
    () => [
      {
        id: "dealDate",
        desc: true,
      },
    ],
    []
  );

  useEffect(() => {
    console.log("rendering")
    setMonthPickerValue(["1998-01-01", moment().startOf('second').format("YYYY-MM-DD")]);
  }, [])

  return (
    <div className="py-2  bg-[#EDEDED] w-[100%]" ref={InvIpoRef}>
      <div className=" mt-4 pb-2 bg-white shadow-[0_2px_6px_0_rgba(0,0,0,0.08)] sm:rounded-lg">
        <div className="pt-4 px-5 border-b border-b-[#F1F1F1] ">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-[14pt] leading-[19pt] text-[#333333] lg:text-[17px] lg:leading-[22px] font-semibold">
              Portfolio Companies - IPOs
            </h3>
            <div className="pr-4 flex flex-row items-center gap-2">
              <p className="px-2 text-[#bc8b20]">
                No of IPOs: {" "}
                <span>
                  {rowData?.length}
                </span>
              </p>
              <MonthPickerComponent />
              {!isDirectoryUser
                ? isStudent === "false" &&
                  isTrialExport !== "false" &&
                  showXlDownload && (
                    <ExcelDownloader
                      url={exportExcel}
                      fileName={Constants.exportNames.portfolio_companies_ipo(
                        investorName
                      )}
                      data={{
                        type: primaryMasterId,
                        export_from: "detail",
                        module_type: "investors",
                        category: "ipos",
                        section: "company_ipos",
                        investor_id: [investorId],
                        date_range: {
                          from_date: moment(monthPickerValue[0]).format("YYYY-MM-DD"),
                          to_date: moment(monthPickerValue[1]).format("YYYY-MM-DD"),
                        }
                      }}
                    />
                  )
                : ""}
            </div>
          </div>
        </div>
        <div className="space-y-6">
          <div className="bg-white pb-2 rounded-[6px] overflow-x-auto scrollbar">
            <AllDataTable
              columns={columns}
              data={rowData}
              sortBy={initialSort}
              showLoader={isLoading}
            />
          </div>
        </div>
      </div>
      {<ConfirmPopup {...alert} onChange={onConfirmation} />}
    </div>
  );
}

export default AllInvestorPortfolioIPO;